/*
 * AN @ REFERENCE ON THE WIRE — the link in a message's text, and the `data-reference` part beside it.
 *
 * Written once because it is read on both sides. The browser writes both forms and reads them back
 * (base-ui `references.ts`); the worker validates the part at the archive's door and reads the link
 * as words for titles, previews and memories. Two copies of a grammar drift — the reader stops
 * matching what the writer makes, and a title shows `](ref:` — and two copies of a limit disagree,
 * so the archive refuses a whole message the composer thought was fine. So the link's writer and
 * its readers, the part's shape, and the limits are all here: one edit moves both sides.
 *
 * Dependency-free, so the worker can import it without the agent loop.
 */

/** An entity the person picked with @, as a message carries it. */
export interface TurnReference {
  /** What it is: `controller`, `model`, `dashboard`, `gateway`, `widget`, `specialist`. */
  kind: string;
  id: string;
  /** The relay it lives on. Absent for what is not on one: a relay itself, a widget, a specialist. */
  gatewayId?: string;
  /** The name the person saw when they picked it. */
  label: string;
  /** What the model was told about it — identity and structure, never live values. Empty for a
   *  reference that tells the model nothing, such as a specialist, which switches the thread. */
  card: string;
}

/** The address of a referenced entity, without what was said about it. */
export type ReferenceAddress = Pick<TurnReference, 'kind' | 'id' | 'gatewayId'>;

/** A kind: a short lower-case word. It is also the first segment of the link's address. */
export const REFERENCE_KIND_RE = /^[a-z][a-z0-9-]{0,31}$/;
/** One card's ceiling — short enough that ten of them are still a small part of a prompt. The
 *  composer cuts a longer card to it; the archive refuses one past it. */
export const REFERENCE_CARD_MAX_CHARS = 1200;
/** The ceiling for an id, a relay id and a stored label. The composer cuts a label to it and does not
 *  offer an entity whose id is longer; the archive refuses either past it. */
export const REFERENCE_FIELD_MAX_CHARS = 512;
/** The link text's ceiling: the name as it reads in the sentence. */
const LINK_LABEL_MAX_CHARS = 120;

/**
 * Why this is not a reference the archive accepts, or '' when it is one.
 *
 * The one statement of the rules. The worker refuses a part that breaks them; the composer checks
 * the same rules before it offers or sends a reference, so a message is never refused for one.
 */
export function referenceProblem(data: unknown): string {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return 'data object required';
  const d = data as Record<string, unknown>;
  if (typeof d.kind !== 'string' || !REFERENCE_KIND_RE.test(d.kind)) return 'kind must be a lower-case word';
  for (const k of ['id', 'label'] as const) {
    const v = d[k];
    if (typeof v !== 'string' || !v) return `${k} required`;
    if (v.length > REFERENCE_FIELD_MAX_CHARS) return `${k} exceeds ${REFERENCE_FIELD_MAX_CHARS} characters`;
  }
  const gw = d.gatewayId;
  if (gw !== undefined && (typeof gw !== 'string' || !gw || gw.length > REFERENCE_FIELD_MAX_CHARS)) {
    return 'gatewayId must be a non-empty string when present';
  }
  if (typeof d.card !== 'string') return 'card must be a string (empty is fine)';
  if (d.card.length > REFERENCE_CARD_MAX_CHARS) return `card exceeds ${REFERENCE_CARD_MAX_CHARS} characters`;
  return '';
}

/** Every character the markdown link grammar or a path split could misread, escaped.
 *  `encodeURIComponent` leaves `(`, `)` and a few others alone, and a `)` would end the link early. */
const enc = (s: string): string => encodeURIComponent(s).replace(/[()!'*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);

/** `ref:<kind>/<gateway>/<id>`, or `ref:<kind>/<id>` for an entity that is not on a relay. */
export function refHref(r: ReferenceAddress): string {
  return `ref:${[r.kind, ...(r.gatewayId ? [r.gatewayId] : []), r.id].map(enc).join('/')}`;
}

/** The inverse of `refHref`. Null for anything that is not a well-formed reference address. */
export function parseRefHref(href: string): ReferenceAddress | null {
  if (!href.startsWith('ref:')) return null;
  const seg = href.slice(4).split('/');
  if (seg.length < 2 || seg.length > 3 || seg.some((s) => !s)) return null;
  try {
    const [kind, a, b] = seg.map(decodeURIComponent) as [string, string, string | undefined];
    if (!REFERENCE_KIND_RE.test(kind)) return null;
    return b === undefined ? { kind, id: a } : { kind, gatewayId: a, id: b };
  } catch {
    return null; // a malformed escape
  }
}

/** The label as it can appear inside a link: no brackets (they would end the link text), no line
 *  breaks, bounded. Falls back to nothing, so the caller can fall back to the id. */
function tokenLabel(label: string): string {
  return label
    .replace(/[[\]\r\n]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, LINK_LABEL_MAX_CHARS);
}

/** What the composer shows for a mention, and what its link reads: `@Conveyor PLC`. */
export const mentionText = (r: Pick<TurnReference, 'label' | 'id'>): string => `@${tokenLabel(r.label) || r.id}`;

/** The link a reference becomes in the message text: `[@Conveyor PLC](ref:controller/gw-1/plc-7)`. */
export function referenceToken(r: TurnReference): string {
  return `[${mentionText(r)}](${refHref(r)})`;
}

/** A reference link in message text: `[label](ref:…)`. Source only — each caller makes its own
 *  RegExp, because a shared global one carries `lastIndex` between calls. It matches what
 *  `referenceToken` writes: the label holds no `]` or line break, and the address no `)` or space. */
export const REF_LINK_SOURCE = String.raw`\[([^\]\n]*)\]\((ref:[^)\s]+)\)`;

/**
 * The text with each reference link read as its label — what a person means by the message.
 *
 * For everything that reads a message as words rather than drawing it: a title, a preview, a memory,
 * copy, read-aloud. Each of those would show `[@Conveyor PLC](ref:…)` where the person wrote a name.
 */
export function plainRefs(text: string): string {
  return text.includes('](ref:') ? text.replace(new RegExp(REF_LINK_SOURCE, 'g'), (_m, label: string) => label) : text;
}
